import { crudOperations } from './api';

class TreatmentReportsService {
  // Get treatment reports for a franchisee
  async getTreatmentReports(franchiseeId, { 
    page = 1, 
    limit = 50, 
    search, 
    status, 
    technician, 
    customer,
    sortBy = 'service_date', 
    sortOrder = 'DESC' 
  } = {}) {
    try {
      const params = { page, limit, sortBy, sortOrder };
      if (search) params.search = search;
      if (status) params.status = status;
      if (technician) params.technician = technician;
      if (customer) params.customer = customer;

      const response = await crudOperations.get(`/treatment-reports/${franchiseeId}`, params);
      return response.data;
    } catch (error) {
      console.error('Get treatment reports error:', error);
      throw error;
    }
  }

  // Get specific treatment report
  async getTreatmentReport(franchiseeId, reportId) {
    try {
      const response = await crudOperations.get(`/treatment-reports/${franchiseeId}/${reportId}`);
      return response.data;
    } catch (error) {
      console.error('Get treatment report error:', error);
      throw error;
    }
  }

  // Create new treatment report
  async createTreatmentReport(franchiseeId, reportData) {
    try {
      const response = await crudOperations.post(`/treatment-reports/${franchiseeId}`, reportData);
      return response.data;
    } catch (error) {
      console.error('Create treatment report error:', error);
      throw error;
    }
  }

  // Update treatment report
  async updateTreatmentReport(franchiseeId, reportId, reportData) {
    try {
      const response = await crudOperations.put(`/treatment-reports/${franchiseeId}/${reportId}`, reportData);
      return response.data;
    } catch (error) {
      console.error('Update treatment report error:', error);
      throw error;
    }
  }

  // Delete treatment report
  async deleteTreatmentReport(franchiseeId, reportId) {
    try {
      const response = await crudOperations.delete(`/treatment-reports/${franchiseeId}/${reportId}`);
      return response.data;
    } catch (error) {
      console.error('Delete treatment report error:', error);
      throw error;
    }
  }

  // Get technicians for dropdown
  async getTechnicians(franchiseeId) {
    try {
      const response = await crudOperations.get(`/treatment-reports/${franchiseeId}/technicians`);
      return response.data;
    } catch (error) {
      console.error('Get technicians error:', error);
      throw error;
    }
  }

  // Get next available report number
  async getNextReportNumber(franchiseeId) {
    try {
      const response = await crudOperations.get(`/treatment-reports/next-report-number/${franchiseeId}`);
      return response.data;
    } catch (error) {
      console.error('Get next report number error:', error);
      throw error;
    }
  }

  // Generate PDF for treatment report
  async generatePDF(franchiseeId, reportId) {
    try {
      const response = await crudOperations.post(`/treatment-reports/${franchiseeId}/${reportId}/generate-pdf`);
      return response.data;
    } catch (error) {
      console.error('Generate PDF error:', error);
      throw error;
    }
  }

  // Upload a treatment report photo via FormData (avoids WAF blocking base64 in JSON)
  async uploadPhoto(file) {
    try {
      const formData = new FormData();
      formData.append('photo', file, file.name);
      const response = await crudOperations.upload('/treatment-reports/upload-photo', formData);
      return response.data;
    } catch (error) {
      console.error('Upload photo error:', error);
      throw error;
    }
  }

  // Sign treatment report (capture customer signature)
  // Uses FormData file upload to avoid mod_security WAF blocking base64 in JSON
  async signReport(franchiseeId, reportId, signatureData) {
    try {
      // Convert base64 data URL to Blob
      const base64 = signatureData.signature_data.replace(/^data:image\/png;base64,/, '');
      const byteChars = atob(base64);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteArray[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: 'image/png' });

      const formData = new FormData();
      formData.append('signature', blob, 'signature.png');
      formData.append('signer_name', signatureData.signer_name);

      const response = await crudOperations.upload(`/treatment-reports/${franchiseeId}/${reportId}/sign`, formData);
      return response.data;
    } catch (error) {
      console.error('Sign treatment report error:', error);
      throw error;
    }
  }

  // AI polish text for treatment report fields
  async polishText(text, field) {
    try {
      const response = await crudOperations.post('/treatment-reports/ai-polish', { text, field });
      return response.data;
    } catch (error) {
      console.error('AI polish error:', error);
      throw error;
    }
  }

  // Search customers for lookup (uses existing customer service endpoint)
  async searchCustomers(franchiseeId, query, limit = 500) {
    try {
      const response = await crudOperations.get(`/customers/${franchiseeId}/search`, { q: query, limit });
      return response.data;
    } catch (error) {
      console.error('Search customers error:', error);
      throw error;
    }
  }

  // Search products/services for lookup (uses existing products-services endpoint)
  async searchProductsServices(query, limit = 10) {
    try {
      const response = await crudOperations.get('/products-services/search/autocomplete', { q: query, limit });
      return response.data;
    } catch (error) {
      console.error('Search products/services error:', error);
      throw error;
    }
  }

  async getAllProductsServices() {
    try {
      const response = await crudOperations.get('/products-services');
      return response.data?.data?.products || [];
    } catch (error) {
      console.error('Get all products/services error:', error);
      throw error;
    }
  }

  // Get treatment products (grouped by category, for treatment report product picker)
  async getTreatmentProducts() {
    try {
      const response = await crudOperations.get('/treatment-products');
      return response.data?.data?.products || [];
    } catch (error) {
      console.error('Get treatment products error:', error);
      throw error;
    }
  }
}

const treatmentReportsService = new TreatmentReportsService();
export default treatmentReportsService;